"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { useSiteInfo } from "@context/siteInfoContext";
import type { ComponentProps, ReactNode } from "react";
import { useContext, useMemo } from "react";

import { cn } from "@/lib/utils";
import { getMatchingCampaigns } from "@/utils/product/getMatchingCampaigns";
import getSortedCampaigns from "@/utils/product/getSortedCampaigns";
import { shouldShowCampaignBadge } from "@/utils/product/shouldShowCampaignBadge";

import { BRAND_CODE } from "@/config/common";
import { useMarket } from "@app/_context";
import ProductCardBadge from "./ProductCardBadge";
import { ProductCardContext } from "./ProductCardContext";

type ProductCardBadgesProps = {
  children?: ReactNode;
};

const ProductCardBadges = ({ children }: ProductCardBadgesProps) => {
  const t = useTranslate();
  const { product, hoverImage, hideHoverImage } = useContext(ProductCardContext);
  const { campaigns, campaignPriorityCollection } = useSiteInfo();
  const { state } = useMarket();
  const productBadge = useMemo(() => {
    if (!product) {
      return undefined;
    }

    const campaignsPriorityOrder =
      campaignPriorityCollection?.items.map((campaign) => campaign?.sys.id) ??
      [];

    const matchingCampaigns = getMatchingCampaigns({
      campaigns: campaigns ?? [],
      flags: product.flags ?? [],
    });

    const sortedCampaigns = getSortedCampaigns({
      campaigns: matchingCampaigns,
      order: campaignsPriorityOrder,
    });

    return {
      // Only show the first campaign badge after sorting them based on priority
      campaigns: [sortedCampaigns?.[0]],
      isNew:
        product.flags?.includes("news") ||
        product.flags?.includes(`${BRAND_CODE}_${state.market.code}_new`),
      isLimited: product.flags?.includes("limited_edition"),
    };
  }, [product, campaigns, campaignPriorityCollection, state.market.code]);

  const badgeClassNameOnHover = () => {
    // Do not animate badges on hover if there is no hover image
    if (!hoverImage || hideHoverImage) return null;

    return "transition-opacity group-hover:opacity-0";
  };

  return (
    <>
      <ProductCardBadge
        x="start"
        y="start"
        className="flex flex-wrap content-start items-start gap-1 p-2"
      >
        {productBadge?.campaigns?.map(
          (campaign) =>
            campaign?.campaignBadge &&
            shouldShowCampaignBadge({
              priceType: product?.priceType,
              campaign,
            }) && (
              <ProductCardBadgeLabel key={campaign.campaignBadge}>
                {campaign.campaignBadge}
              </ProductCardBadgeLabel>
            ),
        )}
        {productBadge?.isNew && (
          <ProductCardBadgeLabel>
            {t("new") || "New"}
          </ProductCardBadgeLabel>
        )}
      </ProductCardBadge>
      <ProductCardBadge
        y="end"
        className={cn(
          "px-4 py-3",
          badgeClassNameOnHover(),
          "flex w-full",
          productBadge?.isLimited && "mb-3",
        )}
      >
        {product?.hasVariants && (
          <span className="ml-auto">{t("moreVariants")}</span>
        )}
      </ProductCardBadge>
      {productBadge?.isLimited && (
        <ProductCardBadge className="inset-x-0 bottom-0 bg-primary py-0.5 text-center font-normal text-black">
          {t("limitedEdition") || "NOGA Exclusives"}
        </ProductCardBadge>
      )}
      {children}
    </>
  );
};

function ProductCardBadgeLabel({
  children,
  className,
  ...props
}: ComponentProps<"span">) {
  return (
    <span
      className={cn(
        // https://css-tricks.com/multi-line-padded-text/#aa-fabien-doirons-box-shadow-method
        "inline whitespace-nowrap bg-white decoration-clone px-2 py-1 text-sm font-normal leading-snug tracking-wide text-black last-of-type:mr-0",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

ProductCardBadges.displayName = "ProductCardBadges";

export default ProductCardBadges;
